// 간단한 토스트 — 전역 Provider + useToast 훅.
// 5초 후 자동 사라짐. 동시에 여러 개 표시 가능.

import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';

export type ToastKind = 'info' | 'success' | 'error';

interface ToastItem {
  id: number;
  kind: ToastKind;
  message: string;
}

interface ToastContextValue {
  push: (kind: ToastKind, message: string) => void;
  success: (message: string) => void;
  error: (message: string) => void;
  info: (message: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within ToastProvider');
  return ctx;
}

let nextId = 0;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);

  const remove = useCallback((id: number) => {
    setItems((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const push = useCallback(
    (kind: ToastKind, message: string) => {
      // 동일 종류·동일 메시지가 이미 떠 있으면 중복 추가하지 않는다 — 연속 클릭 시 스택 누적 방지.
      setItems((prev) => {
        if (prev.some((t) => t.kind === kind && t.message === message)) return prev;
        const id = ++nextId;
        window.setTimeout(() => remove(id), 5000);
        // 한 번에 최대 5개까지만 표시 (초과 시 오래된 것 제거)
        const next = [...prev, { id, kind, message }];
        return next.length > 5 ? next.slice(next.length - 5) : next;
      });
    },
    [remove],
  );

  const value = useMemo<ToastContextValue>(
    () => ({
      push,
      success: (m) => push('success', m),
      error: (m) => push('error', m),
      info: (m) => push('info', m),
    }),
    [push],
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="toast-stack" aria-live="polite">
        {items.map((t) => (
          <div key={t.id} className={`toast toast--${t.kind}`} onClick={() => remove(t.id)}>
            {t.message}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}
